import type { Container, ContainerItem, Item } from "@egalregal/shared";
import type { OwnCatalogEntry, SharedCatalogEntry } from "../../shared/api/requests";
import { readableContainers, useAppStore } from "../../shared/store/app";
import { getMirroredItems, getMirroredPlacements } from "../search/catalogStore";
import { entryIdOfContainer } from "../catalogs/entryLabel";
import { parseLabelUuid } from "./label";

/**
 * The browser-mirror snapshot a scan resolves against: the visible item union
 * plus the placement links and the readable container union that give each
 * item a location and an owner (ADR-0026, qr-item-tags 01).
 */
export interface ItemResolutionScope {
  items: readonly Item[];
  placements: readonly ContainerItem[];
  containers: readonly Container[];
  entries: readonly OwnCatalogEntry[];
  sharedWithMe: readonly SharedCatalogEntry[];
  selectedEntryId?: string;
}

/**
 * Resolve a scanned barcode among the visible items. Fresh UUID labels make a
 * collision practically unreachable, but when several visible items share a
 * barcode the outcome must not depend on Map order: own item first, then an
 * item placed in a container of the selected catalog entry, then any other
 * visible item; equal ranks break on the item id.
 *
 * "Own" is derived from the placement containers' catalog entries: an item
 * with no placement is own (the server mirrors only own unplaced items), and a
 * placed item is own when every placement container belongs to one of the
 * caller's own entries. Mirrored barcodes compare case-insensitively only when
 * both sides are canonical UUID labels (legacy uppercase label barcodes); every
 * other code stays an exact match.
 *
 * @param barcode the scanned/typed item barcode.
 * @param scope the mirrored visibility snapshot.
 * @returns the winning visible item, or undefined when the barcode is unknown.
 */
export function resolveItemByBarcode(
  barcode: string,
  scope: ItemResolutionScope,
): Item | undefined {
  const candidates = scope.items.filter((item) => barcodesMatch(barcode, item.barcode));
  if (candidates.length <= 1) return candidates[0];
  const ownEntryIds = new Set(scope.entries.map((entry) => entry.id));
  return [...candidates].sort(
    (a, b) =>
      rankOf(a, scope, ownEntryIds) - rankOf(b, scope, ownEntryIds) ||
      (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  )[0];
}

function rankOf(
  item: Item,
  scope: ItemResolutionScope,
  ownEntryIds: ReadonlySet<string>,
): 0 | 1 | 2 {
  const placements = scope.placements.filter((p) => p.itemId === item.id);
  if (placements.length === 0) return 0;
  const entryIds = placements.map((p) =>
    entryIdOfContainer(scope.containers, scope.entries, scope.sharedWithMe, p.containerId),
  );
  if (entryIds.every((id) => id !== undefined && ownEntryIds.has(id))) return 0;
  if (scope.selectedEntryId !== undefined && entryIds.includes(scope.selectedEntryId)) return 1;
  return 2;
}

/**
 * Compare a scanned code with a stored barcode: label UUIDs may differ only in
 * case (legacy uppercase tags), everything else must match exactly so barcode
 * identity keeps its case-sensitive contract.
 */
function barcodesMatch(scanned: string, stored: string): boolean {
  const scannedLabel = parseLabelUuid(scanned);
  const storedLabel = parseLabelUuid(stored);
  return scannedLabel !== null && storedLabel !== null ? scannedLabel === storedLabel : scanned === stored;
}

/**
 * Resolve a scanned barcode against the live browser mirror and app store.
 * The single seam both scan surfaces use, so `/scan` and the header/search
 * dialog cannot disagree about which item a code names.
 *
 * @param barcode the normalized scan result (lowercase for UUID labels).
 * @returns the winning visible item, or undefined when the barcode is unknown.
 */
export function resolveMirroredItem(barcode: string): Item | undefined {
  const state = useAppStore.getState();
  return resolveItemByBarcode(barcode, {
    items: getMirroredItems(),
    placements: getMirroredPlacements(),
    containers: readableContainers(state),
    entries: state.entries,
    sharedWithMe: state.sharedWithMe,
    selectedEntryId: state.selectedEntryId,
  });
}

/**
 * Resolve a scanned or typed code against the mirror: the barcode contract
 * first, then the item's own UUID. An item's identity label carries its UUID
 * (ADR-0027), and the manual field's item-UUID suggestion submits exactly that
 * id (scan-followups 03), so both must land on the same item — never on the
 * create chooser just because the code is not a barcode.
 *
 * @param code the scanned/typed code (barcode or item UUID).
 * @returns the matching visible item, or undefined when the code is unknown.
 */
export function resolveMirroredCode(code: string): Item | undefined {
  return resolveMirroredItem(code) ?? getMirroredItems().find((item) => item.id === code);
}
